/**
 * The shared milk-share editing pieces — extracted from
 * `prefill-settings-card.tsx` so that BOTH milk-split surfaces render the
 * SAME editor over the SAME rows:
 *
 *   - OVS campuses: the nested milk rows under the Milk (BEVERAGE) kind
 *     inside the Pre-fill settings card's split grid (`MilkShareRows`).
 *   - Straight Serve campuses: the standalone "Milk split" settings card
 *     (`MilkShareGrid` wraps the same rows in their own table) — SS campuses
 *     have no component split, but since PR #2153/#2166 they DO derive one
 *     system milk per meal, apportioned across the lineup SKUs by the very
 *     same `MilkShareRate` rows these cells write.
 *
 * Copying the grid instead of extracting it would have created the second
 * "% setting" that must not exist (Sheila, 2026-08-03: "No duplicate, use
 * single % setting that impacts both auto order and manual order").
 *
 * ⚠ Everything here preserves the two load-bearing invariants documented at
 * length in `prefill-settings-card.tsx`, `split-provenance.ts` and
 * `split-draft.ts`:
 *   1. An inherited value that is merely DISPLAYED is never persisted. Since
 *      2026-08-26 that is enforced by `draftActionFor` (a cell is written only
 *      when the typed number DIFFERS from what it already resolves to) rather
 *      than by rendering inherited numbers as unusable placeholders — the
 *      placeholders made every inherited cell read as uneditable.
 *   2. Milk shares resolve as a WHOLE SET per tier — the first override of a
 *      cell writes the complete set, and clearing clears the complete set.
 *
 * ⚠ `CellProvenance` renders Radix tooltips and mounts its OWN
 * `TooltipProvider`. Radix THROWS without one and blanks the whole route; a
 * "consumers must provide" rule was missed by district-split and the pre-fill
 * card (prod, 2026-08-24) and then by the auto-order wizard (PROD-1623).
 */

import { milkOptionLabel } from '@ordo/constants/milk-options'
import { Button } from '@ordo/ui/button'
import { Input } from '@ordo/ui/input'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ordo/ui/tooltip'
import { RotateCcw } from 'lucide-react'

import {
  clearMilkShareSet,
  type LocationMilkShareRate,
  type MilkProfile,
  type MilkSku,
  type RateScopeType,
  upsertMilkShareRate,
  type Weekday,
} from '@/api/location-settings'
import { type Mealtime, MEALTIME_LABELS } from '@/lib/mealtime'
import { type OrderColumn, WHOLE_SCHOOL_COLUMN_ID } from '@/lib/school-orders/columns'
import { evenSplitPct, rateRowColumnKey, reconcileMilkProfile, shareSumPct } from '@/lib/school-orders/milk'
import {
  bpsToPercentString,
  cellDisplayValue,
  clearedCellValue,
  draftActionFor,
  effectiveCellBps,
  isDraftValueInvalid,
  percentStringToBps,
  type SplitDraftAction,
} from '@/lib/school-orders/split-draft'
import {
  milkSetWriteForOverride,
  resolveShareCells,
  type SplitCell,
  splitSourceLabel,
} from '@/lib/school-orders/split-provenance'

import type { SplitEditor } from './split-editor'

/**
 * Which tier a grid writes. The campus editors leave this at its default; the
 * district-defaults dialog passes `ORGANIZATION` + the Organization id, and
 * the weekday strip passes `LOCATION_WEEKDAY` + a weekday.
 */
export interface SplitWriteScope {
  scopeType?: RateScopeType
  scopeId?: string
  weekday?: Weekday | null
}

/**
 * The wire scope tuple for one order column: AGE_GROUP columns carry the
 * `LocationGradeRange` column id as the value; group columns carry their
 * own (`groupDimension`, `groupValue`) tuple. The whole-school column
 * (NONE campuses) is bucket-less — its rates persist as location-wide
 * rows (null tuple) rather than a fabricated AGE_GROUP tuple.
 */
export function columnScope(column: OrderColumn): {
  groupDimension: NonNullable<OrderColumn['groupDimension']> | 'AGE_GROUP' | null
  groupValue: string | null
} {
  if (column.groupDimension && column.groupValue) {
    return { groupDimension: column.groupDimension, groupValue: column.groupValue }
  }
  if (column.columnId === WHOLE_SCHOOL_COLUMN_ID) {
    return { groupDimension: null, groupValue: null }
  }
  return { groupDimension: 'AGE_GROUP', groupValue: column.columnId }
}

/** The cell's column key half — the whole-school column edits the wide (null-tuple) rows. */
export function columnCellKey(column: OrderColumn): string {
  return column.columnId === WHOLE_SCHOOL_COLUMN_ID ? 'default' : column.columnId
}

/** Draft key for one milk cell — unique across a dialog's whole grid. */
export function milkCellKey(mealtime: Mealtime, columnId: string, componentId: string): string {
  return `milk::${mealtime}::${columnId}::${componentId}`
}

/** The inherited/overridden chip plus a reset-to-inherited action for one cell. */
export function CellProvenance({
  cell,
  onClear,
  clearLabel,
  pending = false,
}: {
  cell: SplitCell
  onClear?: () => void
  clearLabel: string
  /** The cell carries an edit that Save has not written yet. */
  pending?: boolean
}) {
  const label = cell.owned ? 'Set here' : splitSourceLabel(cell.source)
  if (pending) {
    return (
      <span className="text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-500">
        • Unsaved
      </span>
    )
  }
  return (
    <TooltipProvider delayDuration={150}>
      <span className="inline-flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={`text-[10px] uppercase tracking-wide ${cell.owned ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
              {cell.owned ? '• Set here' : label}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {cell.owned
              ? cell.inheritedBps === null
                ? 'Set at this campus. The district has no value for this cell.'
                : `Set at this campus. The district value is ${bpsToPercentString(cell.inheritedBps)}%.`
              : cell.source === null
                ? 'Nobody has set this. Components ship one per meal ordered (100%).'
                : 'Inherited from the district. Type a different number to override it for this campus.'}
          </TooltipContent>
        </Tooltip>
        {cell.owned && onClear ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={onClear}
                aria-label={clearLabel}>
                <RotateCcw className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Reset to the district value</TooltipContent>
          </Tooltip>
        ) : null}
      </span>
    </TooltipProvider>
  )
}

/**
 * One editable % cell.
 *
 * The input holds the cell's EFFECTIVE number as a real value — selectable,
 * backspaceable, arrow-keyable — whether that number is this tier's own or
 * inherited. Nothing is written here: the cell stages its edit with the
 * dialog's `SplitEditor` and the Save button performs the writes.
 *
 * `makeRun` builds the write for an action. Scope (tier, weekday, column,
 * mealtime, rate row vs milk set) is knowledge the CALL SITE has, so the call
 * site supplies the closure.
 */
export function SplitPercentInput({
  cellKey,
  cell,
  editor,
  makeRun,
  ariaLabel,
  clearLabel,
}: {
  cellKey: string
  cell: SplitCell
  editor: SplitEditor
  makeRun: (action: SplitDraftAction) => () => Promise<unknown>
  ariaLabel: string
  clearLabel: string
}) {
  const staged = editor.staged[cellKey]
  const value = staged ? staged.raw : cellDisplayValue(cell)

  return (
    <div className="flex flex-col items-end gap-0.5">
      <div className="flex items-center justify-end gap-2">
        <Input
          type="number"
          step="0.1"
          min={0}
          max={100}
          className={`w-20 text-right ${staged?.invalid ? 'border-destructive' : ''}`}
          value={value}
          aria-invalid={staged?.invalid ? true : undefined}
          onChange={(event) => {
            const raw = event.target.value
            const action = draftActionFor(cell, raw)
            editor.stage(cellKey, {
              raw,
              invalid: isDraftValueInvalid(raw),
              run: action ? makeRun(action) : null,
            })
          }}
          aria-label={ariaLabel}
        />
        <span className="text-xs text-muted-foreground">%</span>
      </div>
      <CellProvenance
        cell={cell}
        pending={staged?.run != null}
        onClear={
          cell.owned
            ? () =>
                editor.stage(cellKey, {
                  raw: clearedCellValue(cell),
                  invalid: false,
                  run: makeRun({ kind: 'clear' }),
                })
            : undefined
        }
        clearLabel={clearLabel}
      />
    </div>
  )
}

/**
 * The nested milk block: a caption stating the school milk profile (the
 * district's chosen milk TYPES — source of truth), then one indented row per
 * linked milk SKU (each a % OF THE MILK TOTAL), plus an inline warning line
 * for any column whose shares don't sum to 100% (saves are never blocked —
 * apportionment normalizes proportionally).
 *
 * The profile is keyed by milk TYPE code and the share rows by SKU/component
 * id; there is no structured SKU→type mapping in the catalog, so we reconcile
 * the two best-effort (by SKU name) and surface gaps as non-blocking hints —
 * without changing how shares are keyed or saved.
 *
 * Renders BARE `<tr>`s — the caller owns the `<table>`. Inside the OVS split
 * grid these sit under the Milk (BEVERAGE) row; `MilkShareGrid` below wraps
 * them in their own table for the Straight Serve settings card.
 */
export function MilkShareRows({
  locationId,
  mealtime,
  milkSkus,
  milkProfile,
  shares,
  inheritedShares,
  columnKeyOf,
  columns,
  editor,
  writeScope,
}: {
  locationId: string
  mealtime: Mealtime
  milkSkus: MilkSku[]
  milkProfile: MilkProfile
  shares: LocationMilkShareRate[]
  inheritedShares: LocationMilkShareRate[]
  columnKeyOf: (row: { groupDimension: string | null; groupValue: string | null }) => string | null
  columns: OrderColumn[]
  editor: SplitEditor
  writeScope?: SplitWriteScope
}) {
  const spanAllColumns = columns.length + 1
  const reconciliation = reconcileMilkProfile(milkSkus, milkProfile.milkOptionCodes)
  const profileLabels = milkProfile.milkOptionCodes.map((code) => milkOptionLabel(code))
  const evenBpsBySku = evenSplitPct(milkSkus.length).map((pct) => pct * 100)
  const skuIds = milkSkus.map((sku) => sku.id)

  /**
   * Milk shares resolve as a WHOLE SET per cascade tier, so this resolves the
   * whole cell at once. `setOwnedHere` is a property of the CELL, not of any
   * one SKU: the campus owns the entire split as soon as it stores any share
   * row for the cell.
   */
  function cellSetFor(column: OrderColumn) {
    return resolveShareCells({
      ownRows: shares,
      inheritedRows: inheritedShares,
      skuIds,
      mealtime,
      columnKey: columnCellKey(column),
      columnKeyOf,
      evenBps: evenBpsBySku,
    })
  }

  const setByColumn = new Map(columns.map((col) => [col.columnId, cellSetFor(col)]))

  /**
   * The number a cell DISPLAYS — the user's unsaved edit when there is one,
   * otherwise its resolved value. The sum warning below has to read the
   * DRAFT, or "these total 90%" would keep describing the saved rows while
   * the user watches themselves type 100.
   */
  function displayedBps(skuId: string, column: OrderColumn): number {
    const staged = editor.staged[milkCellKey(mealtime, column.columnId, skuId)]
    if (staged) {
      const parsed = percentStringToBps(staged.raw)
      if (parsed !== null) {
        return parsed
      }
    }
    const cell = setByColumn.get(column.columnId)?.cells[skuId]
    return cell ? effectiveCellBps(cell) : 0
  }

  const sumPctByColumn = new Map<string, number>(
    columns.map((col) => [
      col.columnId,
      shareSumPct(Object.fromEntries(milkSkus.map((sku) => [sku.id, displayedBps(sku.id, col) / 100]))),
    ]),
  )
  const hasSumWarning = columns.some((col) => Math.round(sumPctByColumn.get(col.columnId) ?? 100) !== 100)

  // Non-blocking reconciliation hints between the profile (source of truth) and
  // the linked SKUs. Only surfaced when the profile actually declares types.
  const profileHints: string[] = []
  if (milkProfile.noMilkMode && milkSkus.length > 0) {
    profileHints.push(
      `The school milk profile says no milk, but ${milkSkus.length} milk SKU${milkSkus.length === 1 ? ' is' : 's are'} linked.`,
    )
  }
  if (reconciliation.profileCodesWithoutSku.length > 0) {
    profileHints.push(
      `No linked milk SKU yet for: ${reconciliation.profileCodesWithoutSku.map((code) => milkOptionLabel(code)).join(', ')}.`,
    )
  }
  if (reconciliation.skusNotInProfile.length > 0) {
    profileHints.push(
      `Linked but not in the school milk profile: ${reconciliation.skusNotInProfile.map((sku) => sku.name).join(', ')}.`,
    )
  }

  return (
    <>
      <tr className="border-b bg-muted/30">
        <td colSpan={spanAllColumns} className="py-2 pl-6 pr-4 text-xs">
          <span className="font-medium text-muted-foreground">School milk profile: </span>
          {milkProfile.noMilkMode ? (
            <span className="text-muted-foreground">Serves no milk.</span>
          ) : profileLabels.length > 0 ? (
            <span className="text-foreground">{profileLabels.join(', ')}</span>
          ) : (
            <span className="text-muted-foreground">Not set — showing all linked milk SKUs.</span>
          )}
        </td>
      </tr>
      {profileHints.map((hint) => (
        <tr key={hint} className="border-b bg-muted/30">
          <td colSpan={spanAllColumns} className="py-1.5 pl-6 pr-4">
            <span className="text-xs text-amber-600 dark:text-amber-500">{hint}</span>
          </td>
        </tr>
      ))}
      {milkSkus.map((sku) => (
        <tr key={sku.id} className="border-b bg-muted/30 last:border-b-0">
          <td className="py-2 pl-6 pr-4 text-muted-foreground">
            {sku.name}
            {sku.source === 'LINEUP' ? (
              <span className="ml-2 text-xs text-muted-foreground/70">menu lineup</span>
            ) : null}
          </td>
          {columns.map((col) => (
            <td key={col.columnId} className="py-2 pl-4 text-right">
              <MilkShareCell
                locationId={locationId}
                componentId={sku.id}
                skuName={sku.name}
                column={col}
                mealtime={mealtime}
                set={setByColumn.get(col.columnId) ?? { cells: {}, setOwnedHere: false }}
                editor={editor}
                writeScope={writeScope}
              />
            </td>
          ))}
        </tr>
      ))}
      {hasSumWarning ? (
        <tr className="border-b bg-muted/30 last:border-b-0">
          <td className="py-1.5 pl-6 pr-4" />
          {columns.map((col) => {
            const sum = Math.round(sumPctByColumn.get(col.columnId) ?? 100)
            return (
              <td key={col.columnId} className="whitespace-nowrap py-1.5 pl-4 text-right">
                {sum !== 100 ? (
                  <span className="text-xs text-amber-600 dark:text-amber-500">
                    Milk shares total {sum}% — scaled to 100% at order time.
                  </span>
                ) : null}
              </td>
            )
          })}
        </tr>
      ) : null}
    </>
  )
}

/**
 * One nested milk-share % cell — the SKU's share OF THE MILK TOTAL for
 * one (mealtime × order column).
 */
function MilkShareCell({
  locationId,
  componentId,
  skuName,
  column,
  mealtime,
  set,
  editor,
  writeScope,
}: {
  locationId: string
  componentId: string
  skuName: string
  column: OrderColumn
  mealtime: Mealtime
  set: { cells: Record<string, SplitCell>; setOwnedHere: boolean }
  editor: SplitEditor
  writeScope?: SplitWriteScope
}) {
  const cell = set.cells[componentId] ?? { value: null, inheritedBps: null, owned: false, source: null }
  const scope = { locationId, mealtime, ...columnScope(column), ...writeScope }
  return (
    <SplitPercentInput
      cellKey={milkCellKey(mealtime, column.columnId, componentId)}
      cell={cell}
      editor={editor}
      makeRun={(action) => async () => {
        if (action.kind === 'clear') {
          // Clears the WHOLE set for the cell: archiving one SKU's row would
          // leave a partial set that still wins this tier.
          await clearMilkShareSet(scope)
          return
        }
        // ⚠ The FIRST override of a cell must persist the COMPLETE set, not
        // just the edited SKU. Shares resolve as a whole set per tier, so a
        // one-row campus set would make the district's other milk types
        // resolve to an implicit 0 — that milk silently stops shipping.
        // Materialising the inherited siblings is therefore required, and it
        // is not a violation of "never persist an inherited value": for milk
        // the SET is the unit of override.
        //
        // The sibling values are read from the editor's LIVE staged map, not
        // from this closure's render-time snapshot. Editing two milk types
        // before pressing Save otherwise lets the second write reinstate the
        // first one's pre-edit value.
        const stagedBpsFor = (skuId: string): number | null => {
          const staged = editor.stagedRef.current[milkCellKey(mealtime, column.columnId, skuId)]
          return staged ? percentStringToBps(staged.raw) : null
        }
        const writes = milkSetWriteForOverride({
          cells: set.cells,
          setOwnedHere: set.setOwnedHere,
          editedComponentId: componentId,
          editedBps: action.bps,
        })
        if (writes === null) {
          await upsertMilkShareRate({ ...scope, componentId, shareBps: action.bps })
          return
        }
        for (const write of writes) {
          const shareBps =
            write.componentId === componentId ? action.bps : (stagedBpsFor(write.componentId) ?? write.shareBps)
          await upsertMilkShareRate({ ...scope, componentId: write.componentId, shareBps })
        }
      }}
      ariaLabel={`Milk share for ${skuName} in ${column.label} at ${MEALTIME_LABELS[mealtime]}`}
      clearLabel={`Reset the milk split for ${column.label} at ${MEALTIME_LABELS[mealtime]} to the district values`}
    />
  )
}

/**
 * The standalone milk-share table for ONE mealtime — the body of the
 * Straight Serve "Milk split" card's Edit dialog. Same rows, same cells,
 * same writes as the OVS split grid's nested milk block; only the wrapper
 * table (and its "Milk" header column) is new.
 */
export function MilkShareGrid({
  locationId,
  columns,
  mealtime,
  milkSkus,
  milkProfile,
  shares,
  inheritedShares,
  editor,
  writeScope,
}: {
  locationId: string
  columns: OrderColumn[]
  mealtime: Mealtime
  milkSkus: MilkSku[]
  milkProfile: MilkProfile
  shares: LocationMilkShareRate[]
  inheritedShares: LocationMilkShareRate[]
  editor: SplitEditor
  writeScope?: SplitWriteScope
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="py-2 pr-4 font-medium">Milk</th>
            {columns.map((col) => (
              <th key={col.columnId} className="whitespace-nowrap py-2 pl-4 text-right font-medium">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <MilkShareRows
            locationId={locationId}
            mealtime={mealtime}
            milkSkus={milkSkus}
            milkProfile={milkProfile}
            shares={shares}
            inheritedShares={inheritedShares}
            columnKeyOf={rateRowColumnKey}
            columns={columns}
            editor={editor}
            writeScope={writeScope}
          />
        </tbody>
      </table>
    </div>
  )
}
